import type { ReactNode } from 'react';

import { ChevronDownIcon, Loader2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type ToolbarMenuItem = {
  label: string;
  icon?: string;
  disabled?: boolean;
  command?: () => void;
  items?: ToolbarMenuItem[];
};

type ToolbarMenuButtonProps = {
  label: string;
  icon?: ReactNode;
  model: ToolbarMenuItem[];
  disabled?: boolean;
  loading?: boolean;
  align?: 'start' | 'center' | 'end';
};

const ItemIcon = ({ icon }: { icon?: string }) =>
  icon ? <i aria-hidden className={`${icon} text-xs`} /> : null;

const renderItems = (items: ToolbarMenuItem[]) =>
  items.map((item) =>
    item.items?.length ? (
      <DropdownMenuSub key={item.label}>
        <DropdownMenuSubTrigger disabled={item.disabled}>
          <ItemIcon icon={item.icon} />
          {item.label}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {renderItems(item.items)}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    ) : (
      <DropdownMenuItem
        key={item.label}
        disabled={item.disabled}
        onClick={() => item.command?.()}
      >
        <ItemIcon icon={item.icon} />
        {item.label}
      </DropdownMenuItem>
    ),
  );

/** Base UI replacement for a PrimeReact SplitButton whose main click has no action. */
export const ToolbarMenuButton = ({
  label,
  icon,
  model,
  disabled,
  loading,
  align = 'start',
}: ToolbarMenuButtonProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      render={
        <Button variant="outline">
          {loading ? <Loader2Icon className="animate-spin" /> : icon}
          {label}
          <ChevronDownIcon />
        </Button>
      }
    />
    <DropdownMenuContent align={align} className="w-auto">
      {renderItems(model)}
    </DropdownMenuContent>
  </DropdownMenu>
);
